// Admin Portal — Rich text for calendar descriptions.
//
// Mirrors fieldiq/src/components/RichText.jsx. The two apps are separate CRA
// builds with separate dependency trees, so this is a copy rather than an
// import; keep the allowlist and link handling in step if either changes.
//
// Google Calendar's description box is a rich-text editor, so its ICS export
// puts HTML directly into DESCRIPTION — there is no X-ALT-DESC in Google's
// feed to fall back to. _sync_ics_feed stores it verbatim in events.agenda, so
// rendering is the only place it can be made safe. The content is
// third-party — anyone who can add an event to the facility's calendar writes
// it — hence an explicit allowlist.

import React from "react";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "a",
  "br",
  "p",
  "ul",
  "ol",
  "li",
];
const ALLOWED_ATTR = ["href", "target", "rel"];

const HAS_TAGS = /<[a-z][^>]*>/i;
const HAS_ENTITIES = /&(?:amp|lt|gt|quot|#0?39|apos|nbsp);/i;

// Tailwind is CDN-loaded (public/index.html), so Preflight is active: it
// strips bullets off lists, margins off paragraphs and colour off links. Every
// allowed tag needs its styling put back or a formatted description renders as
// an unstyled run-on.
const PROSE =
  "[&_p]:mb-2 [&_p:last-child]:mb-0 " +
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ul:last-child]:mb-0 " +
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_ol:last-child]:mb-0 " +
  "[&_li]:mb-0.5 " +
  "[&_a]:text-blue-600 [&_a]:underline [&_a]:break-words " +
  "[&_b]:font-semibold [&_strong]:font-semibold";

const sanitizeDescription = (html) => {
  const frag = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });
  // Force every surviving link out to a new tab, whatever the calendar
  // published. Done by walking the fragment rather than with a
  // DOMPurify.addHook: hooks register on the shared module singleton and would
  // apply to every other sanitize call in the app.
  frag.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  const holder = document.createElement("div");
  holder.appendChild(frag);
  return holder.innerHTML;
};

// Rich-text entry that happens to contain no formatting still arrives
// entity-escaped ("Tom &amp; Jerry"). The decoded string is rendered as a
// React text child, so it is re-escaped on the way out.
const decodeEntities = (text) => {
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
};

export default function RichText({ text, className = "" }) {
  if (!text) return null;

  if (HAS_TAGS.test(text)) {
    return (
      <div
        className={`${className} ${PROSE}`}
        // Allowlist-sanitized directly above.
        dangerouslySetInnerHTML={{ __html: sanitizeDescription(text) }}
      />
    );
  }

  // Plain text: icalendar has already turned the iCal \n escapes into real
  // newlines, so they need preserving — HTML rendering would collapse them.
  return (
    <p className={`${className} whitespace-pre-wrap`}>
      {HAS_ENTITIES.test(text) ? decodeEntities(text) : text}
    </p>
  );
}
